import { useState, useRef, useEffect } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/hooks/use-auth'
import { useWebSocket } from '@/hooks/use-ws'
import { useLang } from '@/hooks/use-lang'
import { useNotifications } from '@/hooks/use-notifications'
import { useIsMobile } from '@/hooks/use-media-query'
import { api, billing } from '@/lib/api'
import { setLang, t, type Lang } from '@/lib/i18n'
import { timeAgo } from '@/lib/time'
import { workspaceUrl, workspaceDisplayName } from '@/lib/workspace'
import { quotaBarColor } from '@/lib/quota'
import type { Notification } from '@/lib/types'
import { Wordmark } from '@/components/wordmark'

// Theme follows OS preference via the prefers-color-scheme media query in
// index.css. No manual toggle: the app inherits whatever the user's system
// reports. Same approach Google uses on its public surfaces.

function Logo({ size = 22 }: { size?: number }) {
  return <img src="/logo.png" width={size} height={size} alt="" aria-hidden="true" />
}

/*
  MonoGlyph — single-character mono-font icon for sidebar nav items.
  Matches the prototype's `~ = ◇ ⇉ ▭ !` glyph aesthetic. The `currentColor`
  inherits from the NavItem's active/inactive state so glyph + label
  always look like one unit.
*/
function MonoGlyph({ c }: { c: string }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-block',
        width: 14,
        textAlign: 'center',
        fontFamily: 'var(--font-mono, ui-monospace, monospace)',
        fontSize: 12,
        lineHeight: 1,
        color: 'currentColor',
        opacity: 0.8,
      }}
    >{c}</span>
  )
}

/*
  ProjectSwitcher — workspace switcher at the top of the sidebar. Lists
  the user's workspaces (GET /api/me/workspaces) and selecting one
  navigates into that workspace's subdomain; the SPA there bootstraps its
  own membership-aware session via /api/auth/refresh, so plain navigation
  is the whole switch. The 'tenant' query is shared with the
  OnboardingGuard — same queryFn shape (api.get with bearer header) so
  React Query's cache holds a real tenant object.
*/
interface WorkspaceSummary {
  tenant_id: string
  name: string
  slug: string
  role: string
  is_personal: boolean
}

function ProjectSwitcher() {
  const { data: current } = useQuery<{ name: string; slug: string; settings?: Record<string, unknown> }>({
    queryKey: ['tenant'],
    queryFn: () => api.get('/api/tenant'),
    staleTime: 60_000,
  })
  const { data: workspaces = [] } = useQuery<WorkspaceSummary[]>({
    queryKey: ['my-workspaces'],
    queryFn: () => api.get<WorkspaceSummary[]>('/api/me/workspaces'),
    staleTime: 60_000,
  })
  const [open, setOpen] = useState(false)
  const name = workspaceDisplayName(current?.name) || t('workspace')

  return (
    <div className="mx-2.5 mt-2 mb-1 relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full px-2.5 py-1.5 rounded-md flex items-center justify-between"
        style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)', cursor: 'pointer' }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate text-sm" style={{ color: 'var(--white)' }}>{name}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.25}
          strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--muted)', flexShrink: 0 }}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <ul role="listbox" className="absolute left-0 right-0 mt-1 z-20 rounded-md py-1"
          style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border)' }}>
          {workspaces.map(w => (
            <li key={w.tenant_id} role="option" aria-selected={w.slug === current?.slug}>
              <a href={workspaceUrl(w.slug)}
                className="flex items-center justify-between px-2.5 py-1.5 text-sm"
                style={{ color: w.slug === current?.slug ? 'var(--accent-ink)' : 'var(--text)' }}>
                <span className="truncate">{workspaceDisplayName(w.name)}</span>
                {w.is_personal && (
                  <span className="text-xs ml-2" style={{ color: 'var(--muted)' }}>{t('personal')}</span>
                )}
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

const LANG_OPTIONS: { code: Lang; flag: string; label: string }[] = [
  { code: 'en', flag: '🇬🇧', label: 'EN' },
  { code: 'fr', flag: '🇫🇷', label: 'FR' },
  { code: 'ru', flag: '🇷🇺', label: 'RU' },
]

function LangSwitcher() {
  const lang = useLang()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  const current = LANG_OPTIONS.find(l => l.code === lang) ?? LANG_OPTIONS[0]

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        title={t('language')}
        className="w-11 h-11 md:w-7 md:h-7 rounded-lg flex items-center justify-center transition text-xs font-medium"
        style={{ color: 'var(--muted)' }}
        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = 'var(--text)'; (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--surface-2)' }}
        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'var(--muted)'; (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent' }}
      >
        {current.flag}
      </button>
      {open && (
        <div className="absolute bottom-9 left-0 z-50 w-32 rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border)' }}>
          {LANG_OPTIONS.map(opt => (
            <button
              key={opt.code}
              onClick={() => { setLang(opt.code); setOpen(false) }}
              className={`w-full flex items-center gap-2 px-3 py-2.5 md:py-2 tap text-xs transition ${
                opt.code === lang ? 'font-semibold' : ''
              }`}
              style={{ color: opt.code === lang ? 'var(--white)' : 'var(--text)' }}
              onMouseEnter={e => (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--surface-2)'}
              onMouseLeave={e => (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'}
            >
              <span>{opt.flag}</span>
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function NotificationBell() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications()

  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  const typeDot: Record<Notification['type'], string> = {
    alert: 'bg-red-500',
    info: 'bg-blue-500',
    warning: 'bg-yellow-400',
  }

  const recent = notifications.slice(0, 10)

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        title={t('notifications')}
        className="w-11 h-11 md:w-7 md:h-7 rounded-lg flex items-center justify-center transition relative"
        style={{ color: 'var(--muted)' }}
        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = 'var(--text)'; (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--surface-2)' }}
        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'var(--muted)'; (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent' }}
      >
        <IconBellOutline />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-red-500 text-[var(--white)] text-[9px] font-bold flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute bottom-9 left-0 z-50 w-[min(18rem,calc(100vw-2rem))] rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border)' }}>
          <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border)' }}>
            <span className="text-sm font-semibold" style={{ color: 'var(--white)' }}>{t('notifications')}</span>
            {unreadCount > 0 && (
              <span className="text-xs" style={{ color: 'var(--muted)' }}>{t('unreadCount', { n: unreadCount })}</span>
            )}
          </div>

          {recent.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm" style={{ color: 'var(--muted)' }}>{t('noNotifications')}</div>
          ) : (
            <div className="max-h-80 overflow-y-auto" style={{ borderBottom: '1px solid var(--border)' }}>
              {recent.map(n => (
                <button
                  key={n.id}
                  onClick={() => {
                    markRead.mutate(n.id)
                    setOpen(false)
                    if (n.link) navigate(n.link)
                  }}
                  className={`w-full text-left px-4 py-3 transition flex items-start gap-3 ${n.read ? 'opacity-60' : ''}`}
                  style={{ borderBottom: '1px solid var(--border)' }}
                  onMouseEnter={e => (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--surface-2)'}
                  onMouseLeave={e => (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'}
                >
                  <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${typeDot[n.type]}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold truncate" style={{ color: 'var(--text)' }}>{n.title}</p>
                    <p className="text-xs mt-0.5 line-clamp-2" style={{ color: 'var(--muted)' }}>{n.message}</p>
                    <p className="text-[10px] mt-1" style={{ color: 'var(--muted)' }}>{timeAgo(n.created_at)}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {recent.length > 0 && unreadCount > 0 && (
            <div className="px-4 py-2.5" style={{ borderTop: '1px solid var(--border)' }}>
              <button
                onClick={() => { markAllRead.mutate(); setOpen(false) }}
                className="text-xs font-medium transition"
                style={{ color: 'var(--accent)' }}
                onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = 'var(--accent-bright)'}
                onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = 'var(--accent)'}
              >
                {t('markAllRead')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function SidebarQuota() {
  const { data: quota } = useQuery({
    queryKey: ['quota'],
    queryFn: () => billing.getQuota(),
    staleTime: 60_000,
  })
  if (!quota) return null
  const used = quota.events_used ?? 0
  const hasCount = quota.events_used !== null && quota.usage_status !== 'count_unavailable'
  const pct = quota.events_limit > 0 && hasCount ? used / quota.events_limit : 0
  const color = hasCount ? quotaBarColor(pct) : 'var(--muted)'
  return (
    <div className="px-4 py-3" style={{ borderTop: '1px solid var(--border)' }}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-medium" style={{ color: 'var(--muted)' }}>{t('eventsThisMonth')}</span>
      </div>
      <div className="w-full h-1 rounded-full" style={{ backgroundColor: 'var(--border)' }}>
        <div className="h-1 rounded-full transition-all duration-500" style={{ width: `${Math.min(pct * 100, 100)}%`, backgroundColor: color }} />
      </div>
      <p className="text-[10px] mt-1 font-medium" style={{ color }}>
        {hasCount
          ? `${used.toLocaleString()} / ${quota.events_limit.toLocaleString()}`
          : `— / ${quota.events_limit.toLocaleString()}`}
      </p>
    </div>
  )
}

function BillingBanners() {
  const { data: quota } = useQuery({
    queryKey: ['quota'],
    queryFn: () => billing.getQuota(),
    staleTime: 60_000,
  })
  const [dismissed, setDismissed] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(sessionStorage.getItem('whl_dismissed_banners') || '{}')
    } catch { return {} }
  })

  if (!quota) return null

  // Banners are dismissed in sessionStorage by default (re-appear on a
  // fresh tab) so a transient quota warning isn't muted forever. The
  // welcome banner is the exception — it should never re-appear once
  // acknowledged, so we mirror that one to localStorage too.
  const PERSISTENT_DISMISS_KEYS = new Set(['trial_welcome'])
  const dismiss = (key: string) => {
    const next = { ...dismissed, [key]: true }
    setDismissed(next)
    sessionStorage.setItem('whl_dismissed_banners', JSON.stringify(next))
    if (PERSISTENT_DISMISS_KEYS.has(key)) {
      try { localStorage.setItem('whl_trial_welcome_seen', 'true') } catch {}
    }
  }

  type BannerTone = 'info' | 'warn' | 'danger' | 'muted'
  type Banner = {
    key: string
    tone: BannerTone
    text: string
    action?: { label: string; href: string }
    dismissible?: boolean
  }
  const banners: Banner[] = []

  /*
    Trial UX is two banners that take turns:

      1. Welcome (first 24h of the trial, if not yet dismissed) — info
         tone, celebrates that the workspace is ready and Pro features
         are active. Dismissed via localStorage so it never reappears
         once acknowledged, even on a different tab/device pair sharing
         the same browser.
      2. Countdown (the rest of the trial) — info → warn → danger as
         the deadline approaches. Dismissible until the last 48h, then
         locked open because visibility matters more than tidiness.

    Welcome takes precedence over countdown when both could fire (day 7
    of a 7-day trial). Once welcome is dismissed, the countdown takes
    over for the same day.
  */
  if (quota.plan === 'starter_trial' && quota.trial_ends_at) {
    const msLeft = new Date(quota.trial_ends_at).getTime() - Date.now()
    const hoursLeft = msLeft / 3_600_000
    const daysLeft = Math.max(0, Math.ceil(hoursLeft / 24))

    // Welcome window: trial just started, daysLeft is at the maximum
    // (i.e., we're in the first 24h after signup). Persists across tabs
    // via localStorage so dismissing on one stops it everywhere.
    let welcomeSeen = false
    try { welcomeSeen = localStorage.getItem('whl_trial_welcome_seen') === 'true' } catch {}
    const inWelcomeWindow = daysLeft >= 7 && msLeft > 0

    if (inWelcomeWindow && !welcomeSeen) {
      banners.push({
        key: 'trial_welcome',
        tone: 'info',
        text: t('trialWelcome'),
        action: { label: t('whatsIncluded'), href: '/settings' },
        dismissible: true,
      })
    } else if (msLeft > 0) {
      const tone: BannerTone = daysLeft >= 4 ? 'info' : daysLeft >= 2 ? 'warn' : 'danger'
      const text = hoursLeft <= 24
        ? t('trialExpiresToday')
        : daysLeft === 1
          ? t('trialOneDayLeft')
          : t('trialDaysLeft').replace('{n}', String(daysLeft))
      banners.push({
        key: 'trial_countdown',
        tone,
        text,
        action: { label: t('upgrade'), href: '/settings' },
        dismissible: daysLeft >= 2,
      })
    }
  }

  // Past due — failed payment, grace period running.
  if (quota.plan === 'past_due') {
    banners.push({
      key: 'past_due',
      tone: 'warn',
      text: t('paymentFailedBanner'),
      action: { label: t('updateBilling'), href: '/settings' },
      dismissible: false,
    })
  }

  // Trial ended (downgraded to community but the trial_ends_at marker survives).
  if (quota.plan === 'community' && quota.trial_ends_at) {
    const trialEnd = new Date(quota.trial_ends_at)
    const daysSinceEnd = (Date.now() - trialEnd.getTime()) / 86_400_000
    if (daysSinceEnd >= 0 && daysSinceEnd <= 30) {
      banners.push({
        key: 'trial_ended',
        tone: 'muted',
        text: t('trialEnded'),
        action: { label: t('upgradeToStarter'), href: '/settings' },
        dismissible: true,
      })
    }
  }

  // Quota near limit (only when we have a count — don't alarm on count_unavailable).
  if (
    quota.events_used !== null &&
    quota.usage_status !== 'count_unavailable' &&
    quota.events_limit > 0 &&
    quota.events_used / quota.events_limit > 0.9
  ) {
    banners.push({
      key: 'quota_near',
      tone: 'danger',
      text: t('quotaNearLimit'),
      dismissible: false,
    })
  }

  const visible = banners.filter(b => b.dismissible === false || !dismissed[b.key])
  if (visible.length === 0) return null

  // Token-driven palette: tone → CSS var triplet, themed in light + dark.
  const palette: Record<BannerTone, { bg: string; border: string; fg: string }> = {
    info:   { bg: 'color-mix(in srgb, var(--accent) 6%, transparent)', border: 'color-mix(in srgb, var(--accent) 18%, transparent)', fg: 'var(--accent)' },
    warn:   { bg: 'color-mix(in srgb, var(--amber) 6%, transparent)',  border: 'color-mix(in srgb, var(--amber) 18%, transparent)',  fg: 'var(--amber)' },
    danger: { bg: 'color-mix(in srgb, var(--red) 6%, transparent)',    border: 'color-mix(in srgb, var(--red) 18%, transparent)',    fg: 'var(--red)' },
    muted:  { bg: 'color-mix(in srgb, var(--fg-3) 8%, transparent)',   border: 'color-mix(in srgb, var(--fg-3) 18%, transparent)',   fg: 'var(--muted)' },
  }

  return (
    <div className="space-y-2 px-4 md:px-6 pt-4">
      {visible.map(b => {
        const p = palette[b.tone]
        return (
          <div
            key={b.key}
            className="flex flex-col items-start gap-2 md:flex-row md:items-center md:gap-3 rounded-lg px-4 py-2.5 text-xs font-medium"
            style={{ backgroundColor: p.bg, border: `1px solid ${p.border}`, color: p.fg }}
          >
            <span className="md:flex-1">{b.text}</span>
            {(b.action || b.dismissible !== false) && (
              <div className="flex items-center gap-4 self-stretch md:self-auto md:gap-3">
                {b.action && (
                  <NavLink
                    to={b.action.href}
                    className="font-semibold underline-offset-2 hover:underline inline-flex items-center tap py-1.5 md:py-0"
                    style={{ color: p.fg }}
                  >
                    {b.action.label}
                  </NavLink>
                )}
                {b.dismissible !== false && (
                  <button
                    onClick={() => dismiss(b.key)}
                    className="opacity-60 hover:opacity-100 transition-opacity text-xs inline-flex items-center tap py-1.5 md:py-0 ml-auto md:ml-0"
                    style={{ color: p.fg }}
                    aria-label={t('dismiss')}
                  >
                    {t('dismiss')}
                  </button>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

/*
  PageBreadcrumb — `<workspace-name> / <page-name>` strip at the top of
  the content area. Mirrors the prototype's "payments-core / Events"
  header. Reads the current route + the cached tenant from React Query.
*/
function PageBreadcrumb() {
  const location = useLocation()
  const { data: tenant } = useQuery<{ slug?: string; name?: string }>({
    queryKey: ['tenant'],
    queryFn: () => api.get('/api/tenant'),
    staleTime: 60_000,
  })

  const segs = location.pathname.split('/').filter(Boolean)
  if (segs.length === 0) return null
  const labelKeys: Record<string, string> = {
    events: 'stream', endpoints: 'endpoints', analytics: 'analytics',
    alerts: 'alerts', 'api-keys': 'apiKeys', audit: 'auditLog',
    settings: 'settings', support: 'support', help: 'help', onboarding: 'onboarding',
  }
  const pageLabel = labelKeys[segs[0]] ? t(labelKeys[segs[0]]) : segs[0]
  const workspaceName = workspaceDisplayName(tenant?.name) || tenant?.slug || t('workspace')

  return (
    <div
      className="px-4 md:px-6 flex items-center gap-1.5 text-xs min-w-0"
      style={{
        height: 36,
        color: 'var(--muted)',
        borderBottom: '1px solid var(--border)',
        backgroundColor: 'var(--bg)',
      }}
    >
      <span className="truncate min-w-0" style={{ color: 'var(--muted)' }}>{workspaceName}</span>
      <span className="shrink-0" style={{ opacity: 0.5 }}>/</span>
      <span className="shrink-0" style={{ color: 'var(--text)', fontWeight: 500 }}>{pageLabel}</span>
    </div>
  )
}

function BetaBanner() {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem('beta_banner_dismissed') === 'true' } catch { return false }
  })

  if (dismissed) return null

  const now = Date.now()
  const betaEnd = new Date('2026-05-01T00:00:00Z').getTime()
  const ended = now >= betaEnd

  return (
    <div className="flex items-center justify-center gap-3 px-4 py-2 text-xs font-semibold shrink-0"
      style={{ background: 'var(--accent-soft)', color: 'var(--accent-ink)', borderBottom: '1px solid var(--line)' }}>
      <span>{ended ? t('betaBannerEnded') : t('betaBannerActive')}</span>
      <button
        onClick={() => { setDismissed(true); localStorage.setItem('beta_banner_dismissed', 'true') }}
        className="ml-2 opacity-60 hover:opacity-100 transition-opacity text-sm leading-none inline-flex items-center justify-center min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0"
        style={{ color: 'var(--accent-ink)' }}
        aria-label={t('dismiss')}
      >&times;</button>
    </div>
  )
}

export function Layout() {
  const { user, logout } = useAuth()
  const { connected } = useWebSocket()
  useLang() // re-render on lang change
  const location = useLocation()
  const isMobile = useIsMobile()
  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?'

  // Below md the docked sidebar collapses into a slide-in drawer.
  const [navOpen, setNavOpen] = useState(false)
  const hamburgerRef = useRef<HTMLButtonElement>(null)
  const drawerRef = useRef<HTMLElement>(null)

  // Close the drawer on navigation and whenever we cross back up to desktop.
  useEffect(() => { setNavOpen(false) }, [location.pathname])
  useEffect(() => { if (!isMobile) setNavOpen(false) }, [isMobile])

  // Open drawer = modal surface: lock body scroll, trap Tab inside, close on
  // Escape, and return focus to the hamburger when it closes.
  useEffect(() => {
    if (!navOpen || !isMobile) return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const focusable = () => {
      const root = drawerRef.current
      if (!root) return [] as HTMLElement[]
      return Array.from(
        root.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter(el => el.offsetParent !== null)
    }
    focusable()[0]?.focus()

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setNavOpen(false); return }
      if (e.key !== 'Tab') return
      const items = focusable()
      if (items.length === 0) return
      const first = items[0]
      const last = items[items.length - 1]
      const active = document.activeElement
      if (e.shiftKey && active === first) { e.preventDefault(); last.focus() }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus() }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = prevOverflow
      hamburgerRef.current?.focus()
    }
  }, [navOpen, isMobile])

  return (
    <div className="flex flex-col h-screen" style={{ backgroundColor: 'var(--bg)' }}>
      {/* Mobile top bar — hamburger + brand, below md only. The docked
          sidebar is hidden at this width, so this is the only nav entry point. */}
      <div
        className="md:hidden flex items-center gap-2 px-3 shrink-0"
        style={{ height: 52, backgroundColor: 'var(--surface)', borderBottom: '1px solid var(--border)' }}
      >
        <button
          ref={hamburgerRef}
          type="button"
          onClick={() => setNavOpen(true)}
          aria-label={t('openMenu')}
          aria-expanded={navOpen}
          aria-controls="app-nav"
          className="inline-flex items-center justify-center min-w-[44px] min-h-[44px] -ml-1 rounded-lg transition"
          style={{ color: 'var(--text)' }}
        >
          <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <Logo size={20} />
        <span className="font-semibold text-sm tracking-tight" style={{ color: 'var(--white)' }}><Wordmark /></span>
      </div>

      <div className="flex flex-1 min-h-0 relative">
        {/* Scrim behind the open drawer (mobile only). Tap to dismiss. */}
        {isMobile && navOpen && (
          <div
            onClick={() => setNavOpen(false)}
            aria-hidden="true"
            className="fixed inset-0 z-40 md:hidden"
            style={{ backgroundColor: 'color-mix(in oklch, var(--fg) 45%, transparent)' }}
          />
        )}

        {/* One sidebar instance: docked (static) at md+, off-canvas drawer
            below md. The transform is gated to max-md so the desktop sidebar
            keeps no transform — otherwise it forms a stacking context and traps
            the footer popovers behind <main>. */}
        <aside
          id="app-nav"
          ref={drawerRef}
          aria-label={t('navigation')}
          role={isMobile ? 'dialog' : undefined}
          aria-modal={isMobile ? navOpen : undefined}
          inert={isMobile && !navOpen}
          className={`fixed inset-y-0 left-0 z-50 w-60 flex flex-col transition-transform duration-200 ease-out md:static md:z-auto ${navOpen ? 'max-md:translate-x-0' : 'max-md:-translate-x-full'}`}
          style={{ backgroundColor: 'var(--surface)', borderRight: '1px solid var(--border)' }}
        >
          <SidebarContent
            connected={connected}
            user={user}
            initials={initials}
            logout={logout}
            onNavigate={() => setNavOpen(false)}
          />
        </aside>

        <main className="flex-1 overflow-auto min-w-0" style={{ backgroundColor: 'var(--bg)' }}>
          <BillingBanners />
          <PageBreadcrumb />
          <Outlet />
        </main>
      </div>
    </div>
  )
}

function SidebarContent({
  connected,
  user,
  initials,
  logout,
  onNavigate,
}: {
  connected: boolean
  user: ReturnType<typeof useAuth>['user']
  initials: string
  logout: () => void
  onNavigate: () => void
}) {
  return (
    <>
      <div className="px-3 py-3.5 flex items-center gap-2" style={{ borderBottom: '1px solid var(--border)' }}>
        <Logo size={20} />
        <span className="font-semibold text-sm tracking-tight" style={{ color: 'var(--white)' }}><Wordmark /></span>
        {/* Drawer close — mobile only; the desktop dock has no close affordance. */}
        <button
          type="button"
          onClick={onNavigate}
          aria-label={t('closeMenu')}
          className="md:hidden ml-auto -mr-1 inline-flex items-center justify-center min-w-[44px] min-h-[44px] rounded-lg transition"
          style={{ color: 'var(--muted)' }}
        >
          <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/*
        Project switcher card — matches the prototype's "Payments core"
        pill at the top of the sidebar. Single-tenant for now (no menu
        on click), but the dropdown caret is a placeholder for the
        future workspace-switching UX.
      */}
      <ProjectSwitcher />

      <nav className="flex-1 px-2.5 py-1 space-y-0.5 overflow-y-auto">
        <NavItem to="/" label={t('stream')} icon={<MonoGlyph c="~" />} end onNavigate={onNavigate}
          badge={
            <span className="flex items-center gap-1 text-xs font-medium" style={{ color: connected ? 'var(--green)' : 'var(--muted)' }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: connected ? 'var(--green)' : 'var(--muted)', animation: connected ? 'ping-soft 2s ease-in-out infinite' : undefined }} />
              {connected ? t('live') : t('off')}
            </span>
          }
        />
        <NavItem to="/endpoints" label={t('endpoints')} icon={<MonoGlyph c="◇" />} onNavigate={onNavigate} />
        <NavItem to="/analytics" label={t('analytics')} icon={<MonoGlyph c="▭" />} onNavigate={onNavigate} />
        <NavItem to="/alerts" label={t('alerts')} icon={<MonoGlyph c="!" />} onNavigate={onNavigate} />
        <NavItem to="/api-keys" label={t('apiKeys')} icon={<MonoGlyph c="⌘" />} onNavigate={onNavigate} />
        <NavItem to="/audit" label={t('audit')} icon={<MonoGlyph c="≡" />} onNavigate={onNavigate} />
        <NavItem to="/settings" label={t('settings')} icon={<MonoGlyph c="⚙" />} onNavigate={onNavigate} />
        <NavItem to="/support" label={t('support')} icon={<MonoGlyph c="?" />} onNavigate={onNavigate} />
      </nav>

      <SidebarQuota />

      <div className="px-4 py-3" style={{ borderTop: '1px solid var(--border)' }}>
        <div className="flex items-center justify-center gap-3 mb-3">
          <LangSwitcher />
          <NotificationBell />
          <NavLink
            to="/help"
            onClick={onNavigate}
            className="w-11 h-11 md:w-7 md:h-7 rounded-lg flex items-center justify-center transition"
            style={({ isActive }) => isActive
              ? { backgroundColor: 'var(--surface-2)', color: 'var(--white)' }
              : { color: 'var(--muted)' }
            }
            onMouseEnter={e => {
              const el = e.currentTarget as HTMLElement
              if (!el.getAttribute('aria-current')) {
                el.style.color = 'var(--text)'
                el.style.backgroundColor = 'var(--surface-2)'
              }
            }}
            onMouseLeave={e => {
              const el = e.currentTarget as HTMLElement
              if (!el.getAttribute('aria-current')) {
                el.style.color = 'var(--muted)'
                el.style.backgroundColor = 'transparent'
              }
            }}
            title={t('help')}
          >
            <IconHelp />
          </NavLink>
        </div>
        <div className="flex items-center justify-between gap-3">
          <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium" style={{ backgroundColor: 'var(--surface-2)', color: 'var(--text)' }}>
            {initials}
          </div>
          <button
            onClick={logout}
            className="text-xs transition inline-flex items-center tap px-2 -mr-2 md:px-0 md:mr-0 rounded-md"
            style={{ color: 'var(--muted)' }}
            onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = 'var(--text)'}
            onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = 'var(--muted)'}
          >
            {t('signOut')}
          </button>
        </div>
      </div>
    </>
  )
}

function NavItem({
  to,
  label,
  icon,
  end,
  badge,
  onNavigate,
}: {
  to: string
  label: string
  icon: React.ReactNode
  end?: boolean
  badge?: React.ReactNode
  onNavigate?: () => void
}) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className="flex items-center gap-3 px-3 py-2.5 md:py-2 tap rounded-lg text-sm transition"
      style={({ isActive }) => isActive
        ? { backgroundColor: 'var(--surface-2)', color: 'var(--white)', fontWeight: 500 }
        : { color: 'var(--muted)' }
      }
      onMouseEnter={e => {
        const el = e.currentTarget as HTMLElement
        if (!el.getAttribute('aria-current')) {
          el.style.color = 'var(--text)'
          el.style.backgroundColor = 'var(--surface-2)'
        }
      }}
      onMouseLeave={e => {
        const el = e.currentTarget as HTMLElement
        if (!el.getAttribute('aria-current')) {
          el.style.color = 'var(--muted)'
          el.style.backgroundColor = 'transparent'
        }
      }}
    >
      {icon}
      <span className="flex-1">{label}</span>
      {badge}
    </NavLink>
  )
}

function IconHelp() {
  return <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
}
function IconBellOutline() {
  return <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" /></svg>
}
